import BigNumber from 'bignumber.js'
import React, {useState,useEffect,useCallback} from 'react'
import Web3 from 'web3'
import styled from 'styled-components'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import { Button, Heading, Text, Link, Toggle } from '@pancakeswap-libs/uikit'
import Input from 'components/Input'
import BalanceInput from 'components/Input/BalanceInput'
import { useERC20 } from 'hooks/useContract'
import { useAllowance } from 'hooks/useAllowance'
import { useTokenApprove } from 'hooks/useApprove'
import { useTokenBalance } from 'hooks/useTokenBalance'
import { getSep20FactoryAddress } from 'utils/addressHelpers'
import useI18n from 'hooks/useI18n'

import { useFeeToken, useCreateSep20TokenBasic } from '../hooks/useSep20Factory'


const Row = styled.div`
  margin-top: 25px;
`;

const Label = styled(Text)`
  text-align: left;
  padding-left: 16px;
  margin-bottom: 5px;
`

const ToggleContainer = styled(Row)`
  display: flex;
  justify-content: left;
  padding-left: 16px;
  align-items: center;
  margin-right: 32px;

  ${Text} {
    margin-right: 8px;
  }
`

const Notification = styled(Text)`
  line-height: 18px;
  padding-top: 10px;
`

interface CreateProps {
  onSuccess?: any
}

const Create: React.FC<CreateProps> = ({ onSuccess }) => {
  const TranslateString = useI18n();
    const { account } = useWallet();

    const [name, setName] = useState('')
    const handleNameChange = (e: React.FormEvent<HTMLInputElement>) => {
      setName(e.currentTarget.value); 
    }
    
    const [symbol, setSymbol] = useState('')
    const handleSymbolChange = (e: React.FormEvent<HTMLInputElement>) => {
      setSymbol(e.currentTarget.value) 
    }
        
    const [decimals, setDecimals] = useState('18')
    const handleDecimalsChange = (e: React.FormEvent<HTMLInputElement>) => {
      setDecimals(e.currentTarget.value)
    }

    const [initialSupply, setInitialSupply] = useState('')
    const handleInitialSupplyChange = (e: React.FormEvent<HTMLInputElement>) => {
      setInitialSupply(e.currentTarget.value)
    }

    const [isMintable, setIsMintable] = useState(false)

    const feeToken:any = useFeeToken();
    const feeTokenString = `${feeToken.creationFee} ${feeToken.symbol}`;
    const feeTokenBalance = useTokenBalance(feeToken.address).div(new BigNumber(10).pow(feeToken.decimals));
    const notEnoughFee = feeTokenBalance.isLessThan(feeToken.creationFee);

    const [requestedApproval, setRequestedApproval] = useState(false)
    const [approved, setApproved] = useState(0)
    const [pendingTx, setPendingTx] = useState(false)

    const tokenContract = useERC20(feeToken.address);
    const allowance = useAllowance(tokenContract, getSep20FactoryAddress(), approved);
    const onApprove = useTokenApprove(tokenContract, getSep20FactoryAddress())
    const allowed = allowance && !allowance.div(new BigNumber(10).pow(feeToken.decimals)).isLessThan(feeToken.creationFee);

    const handleApprove = useCallback(async () => {
        try {
            setRequestedApproval(true)
            const txHash = await onApprove()
            setRequestedApproval(false)
            if (txHash) {
                setApproved(approved+1);
            }
        } catch (e) {
            console.error(e)
        }
    }, [onApprove, approved])

    const onCreate = useCreateSep20TokenBasic();
    const handleSubmit = useCallback(async () => {
        try {
          const txHash = await onCreate(name, symbol, parseInt(decimals), new BigNumber(initialSupply), isMintable);
          if (txHash) onSuccess();
          return txHash;
        } catch (e) {
          console.error(e)
        }

        return false;
      }, [onCreate, name, symbol, decimals, initialSupply, isMintable, onSuccess])

    let invalidString = "";
    if (!name) invalidString = TranslateString(1534,"Invalid Token Name");
    else if (name.length > 40) invalidString = TranslateString(1535,"Token Name Too Long");
    else if (!symbol) invalidString = TranslateString(1536,"Invalid Token Symbol");
    else if (symbol.length > 10) invalidString = TranslateString(1537,"Token Symbol Too Long");
    else if (!decimals || !Number.isInteger(Number(decimals)) || parseInt(decimals) > 20 || parseInt(decimals) < 0 ) invalidString = TranslateString(1538,"Invalid Decimals");
    else if (!initialSupply || Number.isNaN(Number(initialSupply)) || Number(initialSupply)<0 || new BigNumber(initialSupply).isGreaterThan(1e15) ) invalidString = TranslateString(1539,"Invalid Supply");

    const renderButton = () => {
        if (!allowed && !notEnoughFee) {
            return (
                <Button disabled={requestedApproval} onClick={handleApprove}>
                    {requestedApproval?TranslateString(330,"Approving"):TranslateString(316,"Approve")} {feeToken.symbol}
                </Button>
            )
        }
        return (
            <Button disabled={pendingTx || notEnoughFee || !!invalidString} onClick={async () => {
                setPendingTx(true)
                const txHash = await handleSubmit()
                setPendingTx(false)
              }}>
                {notEnoughFee?TranslateString(319,"Insufficient Fee"):(invalidString||TranslateString(320,"Submit"))}
            </Button>
        )
    }
    return (
        <>
            <Row>
              <Label>{TranslateString(410,'Name:')}</Label>
              <Input placeholder={TranslateString(411,'Token Name')} value={name} onChange={handleNameChange} />
            </Row>
            <Row>
              <Label>{TranslateString(412,'Symbol:')}</Label>
              <Input placeholder={TranslateString(415,"Token Symbol")} value={symbol} onChange={handleSymbolChange} />
            </Row>
            <Row>
              <Label>{TranslateString(540,"Decimals (0-20):")}</Label>
              <Input placeholder={TranslateString(416,"Token Decimals")} value={decimals} onChange={handleDecimalsChange} />
            </Row>
            <Row>
              <Label>{TranslateString(539,"Initial Supply (Max: 1 Quadrillion):")}</Label>
              <Input placeholder={TranslateString(413,"Initial Supply")} value={initialSupply} onChange={handleInitialSupplyChange} />
            </Row>
            <ToggleContainer>
              <Text>{TranslateString(414,"Mintable")}:</Text>
              <Toggle checked={isMintable} onChange={() => setIsMintable(!isMintable)} />
            </ToggleContainer>
            <Row>
              <Notification>{TranslateString(315,"Fee")}: {feeTokenString}</Notification>
            </Row>
            <Row>
              {renderButton()}
            </Row>
        </>
    )
}

export default Create
 